import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useState } from "react";
import "./educationRecords.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError
} from "./onboardingFeatureAccess";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../components/ui/popover";

const DEGREE_TYPES = ["Bachelors", "Masters", "Diploma", "PhD", "Certification"];
const VERIFICATION_STATUSES = ["Verified", "Pending", "Rejected"];

const DEGREE_TYPE_OPTION_KEYS = {
  Bachelors: "onboarding.options.degreeType.bachelors",
  Masters: "onboarding.options.degreeType.masters",
  Diploma: "onboarding.options.degreeType.diploma",
  PhD: "onboarding.options.degreeType.phd",
  Certification: "onboarding.options.degreeType.certification"
};

const VERIFICATION_STATUS_OPTION_KEYS = {
  Verified: "onboarding.options.verificationStatus.verified",
  Pending: "onboarding.options.verificationStatus.pending",
  Rejected: "onboarding.options.verificationStatus.rejected"
};

function translateDegreeTypeLabel(value) {
  const key = DEGREE_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateVerificationStatusLabel(value) {
  const key = VERIFICATION_STATUS_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}
const SAVE_ACTION = {
  title: "Save Education Record",
  serviceCode: "HR09A",
  endpoint: "/saas/hrms/emp-education/add",
  method: "POST"
};

const INITIAL_ENTRY = {
  educationId: "",
  institutionName: "",
  degreeType: "",
  fieldOfStudy: "",
  percentage: "",
  startDate: "",
  endDate: "",
  verificationStatus: "",
  isCompleted: true
};

function isValidIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function apiDateToStorageDate(value) {
  if (!value) return "";
  let str = String(value).trim();
  if (str.includes("T")) str = str.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  return "";
}

function displayDateToApiDate(value) {
  const str = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  return undefined;
}

function extractEducationId(body) {
  const candidates = [
    body?.data?.educationId,
    body?.data?.id,
    body?.educationId,
    body?.id
  ];
  const found = candidates.find((value) => value !== null && value !== undefined && String(value).trim() !== "");
  return found != null ? String(found) : "";
}

export default function EducationRecords({ accessToken, tenantCode, initialEmployeeId = "", breadcrumb, onBack, onNext, onSuccess, isTabMode = false, isActive = true, saveTriggerRef }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [entries, setEntries] = useState([{ ...INITIAL_ENTRY }]);
  const [employeeId, setEmployeeId] = useState(String(initialEmployeeId || ""));
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [loading, setLoading] = useState(false);
  const [, setIsUpdate] = useState(false);
  const [entryErrors, setEntryErrors] = useState([{}]);
  const [entryTouched, setEntryTouched] = useState([{}]);
  const [employeeIdError, setEmployeeIdError] = useState("");

  function apiDateToDisplayDate(value) {
    return apiDateToStorageDate(value);
  }

  const fetchEducation = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const response = await fetch(resolveApiUrl(`/saas/hrms/emp-education/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
          'x-tenant-id': String(sessionDB.getTenantId() || ""),
          'x-service-code': 'HR09S',
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        }
      });
      if (response.ok) {
        const body = await response.json();
        const data = body.data || body;
        const list = Array.isArray(data) ? data : data?.content ? data.content : [data];
        const validList = list.filter((item) => item && (item.institutionName || item.schoolName || item.collegeName));
        if (validList.length > 0) {
          setEntries(validList.map((item) => ({
            educationId: String(item.educationId || item.id || ""),
            institutionName: item.institutionName || item.schoolName || item.collegeName || "",
            degreeType: item.degreeType || item.degree || "",
            fieldOfStudy: item.fieldOfStudy || item.majorSubject || item.major || item.field || "",
            percentage: String(item.percentage ?? item.gpaScore ?? item.gpa ?? item.score ?? ""),
            startDate: apiDateToDisplayDate(item.startDate || item.start_date || item.fromDate),
            endDate: apiDateToDisplayDate(item.endDate || item.end_date || item.toDate),
            verificationStatus: item.verificationStatus || item.status || "Verified",
            isCompleted: item.isCompleted !== false && item.completed !== false
          })));
          setEntryErrors(validList.map(() => ({})));
          setEntryTouched(validList.map(() => ({})));
          setIsUpdate(true);
        }
      }
    } catch (err) {
      console.error("Failed to load education records", err);
    }
  }, [initialEmployeeId, tenantCode, accessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setEmployeeId("");
      setEntries([
      {
        educationId: "",
        institutionName: "",
        degreeType: "Bachelor's Degree",
        fieldOfStudy: "",
        percentage: "",
        startDate: "",
        endDate: "",
        verificationStatus: "Verified",
        isCompleted: true
      }]
      );
      setIsUpdate(false);
      setEntryErrors([{}]);
      setEntryTouched([{}]);
      setEmployeeIdError("");
      return;
    }
    setEmployeeId(String(initialEmployeeId));
    fetchEducation();
  }, [isActive, initialEmployeeId, fetchEducation]);

  function updateEntry(index, key, value) {
    setEntries((prev) => {
      const updated = prev.map((entry, idx) => idx === index ? { ...entry, [key]: value } : entry);
      if (entryTouched[index]?.[key]) {
        setEntryErrors((prevErrors) => {
          const next = [...prevErrors];
          next[index] = validateEntry(updated[index]);
          return next;
        });
      }
      return updated;
    });
  }

  function touchEntryField(index, key, valueOverride) {
    setEntryTouched((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [key]: true };
      return next;
    });
    const entry = valueOverride !== undefined ?
      { ...entries[index], [key]: valueOverride } :
      entries[index];
    setEntryErrors((prev) => {
      const next = [...prev];
      next[index] = validateEntry(entry);
      return next;
    });
  }

  function validateEntry(entry) {
    const errors = {};
    if (!entry.institutionName.trim()) {
      errors.institutionName = i18n.t("onboarding.errors.institutionNameRequired");
    }
    if (!entry.degreeType) {
      errors.degreeType = i18n.t("onboarding.errors.degreeTypeRequired");
    }
    if (!entry.fieldOfStudy.trim()) {
      errors.fieldOfStudy = i18n.t("onboarding.errors.fieldOfStudyRequired");
    }
    if (!entry.startDate.trim() || !displayDateToApiDate(entry.startDate)) {
      errors.startDate = i18n.t("onboarding.errors.startDateRequired");
    }
    if (!entry.endDate.trim() || !displayDateToApiDate(entry.endDate)) {
      errors.endDate = i18n.t("onboarding.errors.endDateRequired");
    } else if (
      isValidIsoDate(entry.startDate) &&
      isValidIsoDate(entry.endDate) &&
      entry.endDate < entry.startDate
    ) {
      errors.endDate = i18n.t("onboarding.errors.endBeforeStart");
    }
    return errors;
  }

  function addAnotherEducation() {
    setEntries((prev) => [...prev, { ...INITIAL_ENTRY }]);
    setEntryErrors((prev) => [...prev, {}]);
    setEntryTouched((prev) => [...prev, {}]);
  }

  function validateEntries() {
    const empIdError = !String(employeeId).trim() || !/^\d+$/.test(String(employeeId).trim()) ?
      i18n.t("onboarding.errors.validEmployeeIdRequired") :
      "";
    setEmployeeIdError(empIdError);

    const nextErrors = entries.map((entry) => validateEntry(entry));
    const nextTouched = entries.map(() => ({
      institutionName: true,
      degreeType: true,
      fieldOfStudy: true,
      startDate: true,
      endDate: true
    }));

    setEntryErrors(nextErrors);
    setEntryTouched(nextTouched);

    return !empIdError && nextErrors.every((entryError) => Object.keys(entryError).length === 0);
  }

  async function handleSave() {
    setBanner(null);
    if (!validateEntries()) return;

    setLoading(true);
    try {
      const updatedEntries = [...entries];

      for (let index = 0; index < updatedEntries.length; index += 1) {
        const entry = updatedEntries[index];
        const educationId = String(entry.educationId || "").trim();
        const isExistingRecord = /^\d+$/.test(educationId);

        const payload = {
          empId: Number(String(employeeId).trim()),
          institutionName: entry.institutionName.trim(),
          degreeType: entry.degreeType || undefined,
          fieldOfStudy: entry.fieldOfStudy.trim() || undefined,
          majorSubject: entry.fieldOfStudy.trim() || undefined,
          startDate: displayDateToApiDate(entry.startDate),
          endDate: displayDateToApiDate(entry.endDate),
          percentage: entry.percentage.trim() || undefined,
          gpaScore: entry.percentage.trim() || undefined,
          isCompleted: !!entry.isCompleted,
          verificationStatus: entry.verificationStatus || undefined
        };

        if (isExistingRecord) {
          payload.educationId = Number(educationId);
        }

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === "") delete payload[key];
        });

        const nextEndpoint = isExistingRecord ?
          `/saas/hrms/emp-education/update/${educationId}` :
          SAVE_ACTION.endpoint;
        const nextMethod = isExistingRecord ? "PUT" : SAVE_ACTION.method;
        const nextServiceCode = isExistingRecord ? "HR09U" : SAVE_ACTION.serviceCode;

        const response = await fetch(resolveApiUrl(nextEndpoint), {
          method: nextMethod,
          headers: {
            "Content-Type": "application/json",
            "x-tenant-code": tenantCode || "",
            "x-service-code": nextServiceCode,
            authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response);
          handleOnboardingApiError(errorBody, errorMessage, { setBanner });
          return;
        }

        if (!isExistingRecord) {
          const body = await response.json().catch(() => ({}));
          const savedEducationId = extractEducationId(body);
          if (savedEducationId) {
            updatedEntries[index] = { ...entry, educationId: savedEducationId };
          }
        }
      }

      setEntries(updatedEntries);
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.educationRecordsSavedSuccessfully") });
      await fetchEducation();
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setBanner });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`eer-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="eer-main">
        {!isTabMode && breadcrumb &&
        <div className="eer-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="eer-bc-sep">/</span>}
                <button
              className={`eer-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="eer-page-header">
            <h1 className="eer-page-title"><Trans i18nKey="onboarding.educationRecords" /></h1>
            <p className="eer-page-sub"><Trans i18nKey="dateTime.trackAcademicQualificationsDurationAndVerificationStat" /></p>
          </div>
        }

        {entries.map((entry, index) =>
        <section key={index} className="eer-entry-card">
            <div className="eer-entry-title"><Trans i18nKey="onboarding.education" />{index + 1}</div>
            <div className="eer-grid">
              {index === 0 ?
            <div className="eer-field">
                  <label className="eer-label"><Trans i18nKey="common.employeeId" /></label>
                  <input className={`eer-input${employeeIdError ? " error" : ""}`} placeholder={i18n.t("common.employeeId")} value={employeeId} onChange={(event) => {
                    setEmployeeId(event.target.value);
                    if (employeeIdError) setEmployeeIdError("");
                  }} readOnly={Boolean(initialEmployeeId)} />
                  <p className="eer-error">{employeeIdError || ""}</p>
                </div> :
            null}
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="onboarding.institutionName" /></label>
                <input className={`eer-input${entryTouched[index]?.institutionName && entryErrors[index]?.institutionName ? " error" : ""}`} placeholder={i18n.t("onboarding.universityCollege")} value={entry.institutionName} onChange={(event) => updateEntry(index, "institutionName", event.target.value)} onBlur={() => touchEntryField(index, "institutionName")} />
                <p className="eer-error">{entryTouched[index]?.institutionName ? entryErrors[index]?.institutionName || "" : ""}</p>
              </div>
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="onboarding.degreeType" /></label>
                <div className="eer-select-wrap">
                  <select className={`eer-select${entryTouched[index]?.degreeType && entryErrors[index]?.degreeType ? " error" : ""}`} value={entry.degreeType} onChange={(event) => updateEntry(index, "degreeType", event.target.value)} onBlur={() => touchEntryField(index, "degreeType")}>
                    <option value=""><Trans i18nKey="onboarding.selectDegree" /></option>
                    {DEGREE_TYPES.map((option) => <option key={option} value={option}>{translateDegreeTypeLabel(option)}</option>)}
                  </select>
                </div>
                <p className="eer-error">{entryTouched[index]?.degreeType ? entryErrors[index]?.degreeType || "" : ""}</p>
              </div>
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="onboarding.fieldOfStudy" /></label>
                <input className={`eer-input${entryTouched[index]?.fieldOfStudy && entryErrors[index]?.fieldOfStudy ? " error" : ""}`} placeholder={i18n.t("onboarding.computerScience")} value={entry.fieldOfStudy} onChange={(event) => updateEntry(index, "fieldOfStudy", event.target.value)} onBlur={() => touchEntryField(index, "fieldOfStudy")} />
                <p className="eer-error">{entryTouched[index]?.fieldOfStudy ? entryErrors[index]?.fieldOfStudy || "" : ""}</p>
              </div>
              <div className="eer-field">
                <label className="eer-label"><Trans i18nKey="onboarding.percentageGpa" /></label>
                <input className="eer-input" placeholder={i18n.t("onboarding.percentagePlaceholder")} value={entry.percentage} onChange={(event) => updateEntry(index, "percentage", event.target.value)} />
              </div>
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="dateTime.startDate" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`eer-input${entryTouched[index]?.startDate && entryErrors[index]?.startDate ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {entry.startDate && isValidIsoDate(entry.startDate) ?
                        format(parse(entry.startDate, "yyyy-MM-dd", new Date()), "PPP") :
                        <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1980, 0)}
                      endMonth={new Date()}
                      selected={isValidIsoDate(entry.startDate) ? parse(entry.startDate, "yyyy-MM-dd", new Date()) : undefined}
                      disabled={{ after: new Date() }}
                      onSelect={(date) => {
                        if (!date) return;
                        const value = format(date, "yyyy-MM-dd");
                        updateEntry(index, "startDate", value);
                        touchEntryField(index, "startDate", value);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <p className="eer-error">{entryTouched[index]?.startDate ? entryErrors[index]?.startDate || "" : ""}</p>
              </div>
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="dateTime.endDate" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`eer-input${entryTouched[index]?.endDate && entryErrors[index]?.endDate ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {entry.endDate && isValidIsoDate(entry.endDate) ?
                        format(parse(entry.endDate, "yyyy-MM-dd", new Date()), "PPP") :
                        <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1980, 0)}
                      endMonth={new Date()}
                      selected={isValidIsoDate(entry.endDate) ? parse(entry.endDate, "yyyy-MM-dd", new Date()) : undefined}
                      disabled={{
                        after: new Date(),
                        ...(isValidIsoDate(entry.startDate) ? { before: parse(entry.startDate, "yyyy-MM-dd", new Date()) } : {})
                      }}
                      onSelect={(date) => {
                        if (!date) return;
                        const value = format(date, "yyyy-MM-dd");
                        updateEntry(index, "endDate", value);
                        touchEntryField(index, "endDate", value);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <p className="eer-error">{entryTouched[index]?.endDate ? entryErrors[index]?.endDate || "" : ""}</p>
              </div>
              <div className="eer-field">
                <label className="eer-label required"><Trans i18nKey="onboarding.verificationStatus" /></label>
                <div className="eer-select-wrap">
                  <select className="eer-select" value={entry.verificationStatus} onChange={(event) => updateEntry(index, "verificationStatus", event.target.value)}>
                    <option value=""><Trans i18nKey="common.selectStatus" /></option>
                    {VERIFICATION_STATUSES.map((option) => <option key={option} value={option}>{translateVerificationStatusLabel(option)}</option>)}
                  </select>
                </div>
              </div>
              <div className="eer-field">
                <label className="eer-label"><Trans i18nKey="status.completed" /></label>
                <div className="eer-toggle-wrap">
                  <button type="button" className={`eer-toggle${entry.isCompleted ? " active" : ""}`} onClick={() => updateEntry(index, "isCompleted", !entry.isCompleted)}>
                    <span className="eer-toggle-knob" />
                  </button>
                  <span className="eer-toggle-text">{entry.isCompleted ? i18n.t("common.yes") : i18n.t("common.no")}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        <button type="button" className="eer-add-more" onClick={addAnotherEducation}>
          <Trans i18nKey="formActions.addAnotherEducation" />
        </button>

        {banner ? <div className={`eer-banner ${banner.type}`}>{banner.msg}</div> : null}

        {!isTabMode && (onBack || onNext) &&
        <div className="eer-actions">
            {onBack ? <button className="eer-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button> : <span />}
            <button className="eer-primary-btn" type="button" onClick={handleSave} disabled={loading}>
              {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
            </button>
            {onNext ? <button className="eer-secondary-btn" type="button" onClick={onNext}><Trans i18nKey="pagination.next2" /></button> : null}
          </div>
        }
      </main>
    </div>);

}
